import React, { useCallback, useEffect, useRef } from 'react'

import { Configurator, ExperienceId, ProductAttributesProvider, type ConfiguratorOnChangeEvent, type ProductOptionsConfiguratorData } from '@vp/product-options'
import { Box, FlexBox, useScreenClass } from '@vp/swan'
import { useLocalizations } from '@vp/ubik-localizations-provider'

import { useQuantity } from './contexts/QuantityContext'
import { useProject } from '../root/contexts/ProjectContext'
import { useAppState } from '../root/contexts/AppContext'
import { useAlertState } from '../root/contexts/AlertContext'
import { useInventory } from '../root/contexts/InventoryContext'
import { usePricingContext } from '../root/contexts/PricingContext'
import { useTracking } from '../root/contexts/TrackingContext'
import { TrackingLabel } from '../root/contexts/TrackingContext/TrackingConstants'
import { AddToCartValidationToast } from './configuration/AddToCartValidationToast'
import { OutOfStockToast } from './configuration/OutOfStockToast'
import QuantityPrice from './configuration/connectedQuantitySelector/QuantityPrice'
import ProductName from '../root/ProductName'
import { ColorName } from './colorOption/ColorName'
import ProjectTools from './configuration/ProjectTools/ProjectTools'
import { scrollToTopToast } from '../../utils/ScrollHelpers'
import type { HardGoodAvailability } from '../../types/Inventory'
import { useValidatedInitialSelections } from './hooks/useValidatedInitialSelections'

const PRODUCT_OPTIONS_QUANTITY_KEY = 'Quantity'

type ProductOptionsQuantitySliderWrapperProps = {
  configuratorData?: ProductOptionsConfiguratorData
}

export const ProductOptionsQuantitySliderWrapper: React.FC<ProductOptionsQuantitySliderWrapperProps> = ({ configuratorData }) => {
  const { t } = useLocalizations()
  const { updateQuantity } = useQuantity()
  const {
    isPrimaryProject,
    productKey,
    productVersion,
    productName,
    projectNumber,
    projectCount,
    workId,
    mpvId,
    minimumOrderQuantity,
    productViewTracked,
    setProductViewTracked,
    work,
  } = useProject()
  const { primaryProject, country } = useAppState()
  const { initialSelections, isResolved: isInitialSelectionsResolved } = useValidatedInitialSelections({
    productKey,
    productVersion,
    country,
    merchandisingSelections: work?.merchandising?.merchandisingSelections ?? {},
    initialQuantity: work?.merchandising?.quantity,
  })
  const { projectQtyErrors } = useAlertState()
  const inventoryState = useInventory()
  const { isInitialized: isPricingContextInitialized } = usePricingContext()
  const { trackProductViewed } = useTracking()
  const screenClass = useScreenClass()
  const configuratorRef = useRef<{ showFormErrors: (scrollToElement: boolean) => boolean } | null>(null)

  const hasProjectQtyError = projectQtyErrors.includes(workId)
  const isLoading = !inventoryState.isInventoryStateInitialized || !isPricingContextInitialized

  const onChange = useCallback(
    (event: ConfiguratorOnChangeEvent) => {
      const selectedQuantity = Number(event.selections[PRODUCT_OPTIONS_QUANTITY_KEY])
      const isValidQuantity = event.isValid && Number.isFinite(selectedQuantity) && selectedQuantity > 0
      const quantity = isValidQuantity ? selectedQuantity : 0

      updateQuantity({ quantities: quantity, total: quantity }, isValidQuantity)
    },
    [updateQuantity],
  )

  useEffect(() => {
    if (hasProjectQtyError) {
      configuratorRef.current?.showFormErrors(false)
    }
  }, [hasProjectQtyError])

  useEffect(() => {
    if (productViewTracked) return

    if (!isLoading) {
      trackProductViewed(TrackingLabel.QSP_PRODUCT_VIEWED, mpvId, productName, productKey, productVersion)
      setProductViewTracked(true)
    }
  }, [isLoading])

  useEffect(() => {
    if (!isLoading && screenClass === 'xs' && projectCount === 1) {
      scrollToTopToast('qsp-project-quantity-section-alert', 1000, 'nearest')
    } else if (hasProjectQtyError) {
      scrollToTopToast('qsp-project-quantity-section-alert', 0, 'center')
    }
  }, [isLoading, screenClass, hasProjectQtyError, projectQtyErrors])

  const isOutOfStock = (availability: HardGoodAvailability): boolean => {
    return availability.numAvailable < minimumOrderQuantity
  }

  const getExpectedDate = (availability: HardGoodAvailability): Date | undefined => {
    return availability.expectedDate
  }

  return (
    <>
      <Box>
        <FlexBox
          className="qsp-project-quantity-section qsp-project-quantity-section-title-and-tools"
          justifyContent="space-between"
          flexDirection={screenClass === 'xs' ? 'column' : null}
        >
          <Box paddingY={{ xs: 4, sm: 0 }}>
            <ProductName renderAs="h2" />
            {!isPrimaryProject && primaryProject?.productName === productName && screenClass === 'xs' && <ColorName mb={{ md: 4 }} />}
          </Box>
          <ProjectTools />
        </FlexBox>
        {!isPrimaryProject && primaryProject?.productName === productName && screenClass !== 'xs' && (
          <ColorName className="qsp-project-quantity-section" mb={{ md: 4 }} />
        )}
      </Box>
      {!isPrimaryProject && hasProjectQtyError && (
        <Box className="qsp-project-quantity-section qsp-project-quantity-section-alert" my={3}>
          <AddToCartValidationToast key={projectNumber} projectNumber={projectNumber} />
        </Box>
      )}
      {!isLoading && isOutOfStock(inventoryState.inventory as HardGoodAvailability) && (
        <Box className="qsp-project-quantity-section qsp-project-quantity-section-alert" my={3}>
          <OutOfStockToast expectedDate={getExpectedDate(inventoryState.inventory as HardGoodAvailability)} />
        </Box>
      )}
      {!isLoading && <QuantityPrice />}
      <Box className="qsp-project-quantity-section qsp-project-quantity-section-grey" backgroundColor="strong" data-testid="product-options-quantity-selector">
        <Box px={5} py={4}>
          {isInitialSelectionsResolved && (
            <Configurator
              ref={configuratorRef}
              experienceId={ExperienceId.QuantitySelectionPageConfigurator}
              onChange={onChange}
              productKey={productKey}
              productVersion={productVersion}
              ssrData={configuratorData}
            >
              <ProductAttributesProvider initialSelections={initialSelections} />
            </Configurator>
          )}
        </Box>
        {hasProjectQtyError && <span className="visually-hidden">{t('quantity-selector.adjust-quantity')}</span>}
      </Box>
    </>
  )
}
